import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "../App";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, List, Calendar as CalendarIcon, Eye, EyeOff, Ban, Star, User, Mail, Phone, Home } from "lucide-react";
import { formatDate, formatDateWithDay } from "../utils/dateFormat";

const CalendarView = ({ user }) => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [hideBooked, setHideBooked] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Dynamic pricing from DB
  const [pricingConfig, setPricingConfig] = useState(null);
  
  // KJ List for dropdown
  const [kjList, setKjList] = useState([]);
  
  // Booking form data
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    block_unit: "",
    kj_preference: "",
    kj_required: true,
    terms_agreed: false
  });

  // Simple body scroll lock when modal is open
  useEffect(() => {
    if (showBookingForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showBookingForm]);

  useEffect(() => {
    fetchBookings();
    fetchBlackoutDates();
    fetchPublicHolidays();
    fetchKjList();
    fetchPricingConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const fetchBookings = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await axios.get(`${API}/bookings/calendar?year=${year}&month=${month}`);
      setBookings(response.data);
    } catch (error) {
      console.error("Failed to fetch bookings", error);
    }
  };

  const fetchPricingConfig = async () => {
    try {
      const res = await axios.get(`${API}/public/pricing-config`);
      setPricingConfig(res.data);
    } catch {
      // Use defaults
      setPricingConfig({ weekday_day: 90, weekday_night: 90, weekend: 100, ph: 100 });
    }
  };

  const fetchBlackoutDates = async () => {
    try {
      const response = await axios.get(`${API}/blackout-dates`);
      setBlackoutDates(response.data);
    } catch (error) {
      console.error("Failed to fetch blackout dates", error);
    }
  };

  const fetchPublicHolidays = async () => {
    try {
      const response = await axios.get(`${API}/admin/public-holidays`);
      setPublicHolidays(response.data);
    } catch (error) {
      console.error("Failed to fetch public holidays", error);
    }
  };
  
  const fetchKjList = async () => {
    try {
      const response = await axios.get(`${API}/kj-list`);
      // Use kj_details for dropdown with type info
      setKjList(response.data.kj_details || []);
    } catch (error) {
      console.error("Failed to fetch KJ list", error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getSlotStatus = (day, slotType) => {
    if (!day) return null;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const booking = bookings.find(b => b.date === dateStr && b.slot_type === slotType && b.status !== 'cancelled');
    return booking;
  };

  // Get slot restriction status with detailed info
  const getSlotRestriction = (day, slotType) => {
    if (!day) return { restricted: false, reason: "", label: "" };
    
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const now = new Date();
    
    // Slot start times: Afternoon = 1PM (13:00), Evening = 6PM (18:00)
    const slotHour = slotType === 'afternoon' ? 13 : 18;
    const slotStart = new Date(dateStr + 'T' + String(slotHour).padStart(2, '0') + ':00:00');
    
    // Check if slot start time has already passed → "Past"
    if (slotStart < now) {
      return { 
        restricted: true, 
        reason: "past",
        label: "Past",
        message: "This slot has already passed."
      };
    }
    
    // Check 2-hour advance booking rule
    const minBookingTime = new Date(now.getTime() + 2 * 60 * 60 * 1000);
    if (slotStart < minBookingTime) {
      return { 
        restricted: true, 
        reason: "12hour",
        label: "2hr",
        message: "Bookings must be made at least 2 hours in advance. Please select a later slot."
      };
    }
    
    // Available for booking
    return { restricted: false, reason: "", label: "", message: "" };
  };

  // Backward compatibility wrapper
  const isSlotInPast = (day, slotType) => {
    const restriction = getSlotRestriction(day, slotType);
    return restriction.restricted;
  };

  const isDayInPast = (day) => {
    if (!day) return false;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayDate = new Date(dateStr);
    dayDate.setHours(23, 59, 59, 999); // End of day
    const now = new Date();
    return dayDate < now;
  };

  const isPublicHoliday = (day) => {
    if (!day) return { isHoliday: false, description: "" };
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    const holiday = publicHolidays.find(h => h.date === dateStr);
    if (holiday) {
      return { isHoliday: true, description: holiday.description };
    }
    
    return { isHoliday: false, description: "" };
  };

  // Check 12-hour advance booking rule (for click handler)
  const check12HourRule = (day, slotType) => {
    const restriction = getSlotRestriction(day, slotType);
    if (restriction.restricted) {
      return { isBookable: false, message: restriction.message };
    }
    return { isBookable: true, message: "" };
  };

  const isDateBlackedOut = (day) => {
    if (!day) return { isBlackout: false, reason: "" };
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const checkDate = new Date(dateStr);
    
    for (const blackout of blackoutDates) {
      const startDate = new Date(blackout.start_date);
      const endDate = new Date(blackout.end_date);
      
      if (checkDate >= startDate && checkDate <= endDate) {
        return { isBlackout: true, reason: blackout.reason };
      }
    }
    
    return { isBlackout: false, reason: "" };
  };

  const handleSlotClick = async (day, slotType) => {
    // Check if user is logged in - they should use the public flow instead
    if (user) {
      // Check if user is an Admin - show special message
      if (user.is_admin) {
        toast.error(
          "Admins must use the Special Booking tool in the Admin Panel. " +
          "Standard bookings are reserved for residents only.",
          { duration: 6000 }
        );
        return;
      }
      // Regular member message
      toast.info(
        "As a member, please log out to book a slot. " +
        "Members should use the public booking flow like any other resident.",
        { duration: 5000 }
      );
      return;
    }
    
    // Check if date is blacked out
    const blackoutCheck = isDateBlackedOut(day);
    if (blackoutCheck.isBlackout) {
      toast.error(`This date is unavailable: ${blackoutCheck.reason}`);
      return;
    }

    // Check 12-hour advance booking rule
    const ruleCheck = check12HourRule(day, slotType);
    if (!ruleCheck.isBookable) {
      toast.error(ruleCheck.message);
      return;
    }

    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    const booking = getSlotStatus(day, slotType);
    
    if (booking) {
      toast.error("This slot is already booked");
      return;
    }

    // Fetch REAL-TIME pricing from API before showing form
    try {
      const priceResponse = await axios.get(`${API}/pricing/check?date=${dateStr}&slot_type=${slotType}`);
      
      // Double-check 12-hour rule from API response
      if (!priceResponse.data.is_bookable) {
        toast.error(priceResponse.data.booking_restriction || "Bookings must be made at least 2 hours in advance.");
        return;
      }
      
      setSelectedSlot({ 
        date: dateStr, 
        slot_type: slotType, 
        day,
        real_price: priceResponse.data.price,
        is_premium: priceResponse.data.is_premium,
        price_reason: priceResponse.data.reason
      });
      
      // Reset form data
      setFormData({
        name: "",
        email: "",
        mobile: "",
        block_unit: "",
        kj_preference: "",
        kj_required: true,
        terms_agreed: false
      });
      
      setShowBookingForm(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to check slot availability");
    }
  };

  const calculatePrice = (dateStr, slotType) => {
    const pc = pricingConfig || { weekday_day: 90, weekday_night: 90, weekend: 100, ph: 100 };
    const date = new Date(dateStr);
    const weekday = date.getDay(); // 0=Sunday, 6=Saturday
    
    if (weekday === 0 || weekday === 6) return pc.weekend;
    
    const holiday = publicHolidays.find(h => h.date === dateStr);
    if (holiday) return pc.ph;
    
    if (slotType === 'evening') return pc.weekday_night;
    return pc.weekday_day;
  };

  // Check if date is a weekend (for display purposes)
  const isWeekend = (day) => {
    if (!day) return false;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const date = new Date(dateStr);
    const weekday = date.getDay();
    return weekday === 0 || weekday === 6;
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    
    if (!formData.terms_agreed) {
      toast.error("Please agree to the Terms & Conditions");
      return;
    }
    
    if (!formData.name || !formData.email || !formData.mobile || !formData.block_unit) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        name: formData.name,
        email: formData.email.toLowerCase(),
        mobile: formData.mobile,
        block_unit: formData.block_unit,
        date: selectedSlot.date,
        slot_type: selectedSlot.slot_type,
        kj_preference: formData.kj_required ? (formData.kj_preference || null) : null,
        kj_required: formData.kj_required
      };
      
      const response = await axios.post(`${API}/bookings`, bookingData);
      toast.success(`Booking created! Reference: ${response.data.booking_code}`);
      setShowBookingForm(false);
      setSelectedSlot(null);
      fetchBookings();
      
      // Navigate to confirmation page
      navigate('/booking-confirmation/' + response.data.booking_id, {
        state: { booking: response.data }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  const closeBookingForm = () => {
    setShowBookingForm(false);
    setSelectedSlot(null);
    setFormData({
      name: "",
      email: "",
      mobile: "",
      block_unit: "",
      kj_preference: "",
      kj_required: true,
      terms_agreed: false
    });
  };

  const days = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // List view slots
  const getListViewSlots = () => {
    const slots = [];
    const daysInMonth = getDaysInMonth(currentDate).filter(d => d !== null);
    
    daysInMonth.forEach(day => {
      const blackoutCheck = isDateBlackedOut(day);
      
      ['afternoon', 'evening'].forEach(slotType => {
        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const booking = getSlotStatus(day, slotType);
        const holidayCheck = isPublicHoliday(day);
        
        if (!hideBooked || (!booking && !blackoutCheck.isBlackout)) {
          slots.push({
            date: dateStr,
            day,
            slotType,
            booking,
            price: calculatePrice(dateStr, slotType),
            isBlackout: blackoutCheck.isBlackout,
            blackoutReason: blackoutCheck.reason,
            isHoliday: holidayCheck.isHoliday,
            holidayDescription: holidayCheck.description
          });
        }
      });
    });
    
    return slots;
  };

  return (
    <div key={`calendar-${viewMode}`}>
      {/* Warning Banner for Logged-in Users */}
      {user && (
        <div className={`border-2 p-4 mb-6 rounded ${user.is_admin ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-amber-500'}`} data-testid="member-booking-warning">
          {user.is_admin ? (
            <>
              <p className="font-bold text-red-800 flex items-center gap-2">
                <Ban size={20} />
                Admin Booking Restricted
              </p>
              <p className="text-sm text-red-700 mt-1">
                Admins cannot use the public booking flow. 
                To create a booking, use the <a href="/admin" className="underline font-bold hover:text-red-900">Special Booking tool in the Admin Panel</a>.
              </p>
              <p className="text-xs text-red-600 mt-2">
                💡 Standard bookings are reserved for residents only.
              </p>
            </>
          ) : (
            <>
              <p className="font-bold text-amber-800 flex items-center gap-2">
                <CalendarIcon size={20} />
                Viewing Calendar Only
              </p>
              <p className="text-sm text-amber-700 mt-1">
                As a logged-in member, you cannot make bookings from here. 
                To book a karaoke session as a resident, please <button onClick={() => { localStorage.removeItem('token'); window.location.reload(); }} className="underline font-bold hover:text-amber-900">log out</button> and use the public booking form.
              </p>
              <p className="text-xs text-amber-600 mt-2">
                💡 Your assigned KJ sessions are shown in the <a href="/kj-console" className="underline font-bold">KJ Console</a>
              </p>
            </>
          )}
        </div>
      )}
      
      {/* Controls */}
      <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={goToPreviousMonth}
            className="p-3 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors"
            data-testid="prev-month-btn"
          >
            <ChevronLeft size={24} strokeWidth={3} />
          </button>
          <h3 className="text-2xl font-syne font-bold min-w-[200px] text-center notranslate" translate="no">{monthName}</h3>
          <button
            onClick={goToNextMonth}
            className="p-3 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors"
            data-testid="next-month-btn"
          >
            <ChevronRight size={24} strokeWidth={3} />
          </button>
        </div>

        <div className="flex gap-4">
          <button
            key={`hide-booked-${hideBooked}`}
            onClick={() => setHideBooked(!hideBooked)}
            className="brutal-btn-outline flex items-center gap-2 notranslate"
            translate="no"
            data-testid="toggle-booked-btn"
          >
            {hideBooked ? <Eye size={20} /> : <EyeOff size={20} />}
            {hideBooked ? 'SHOW BOOKED' : 'HIDE BOOKED'}
          </button>
          <button
            key={`view-mode-${viewMode}`}
            onClick={() => setViewMode(viewMode === 'calendar' ? 'list' : 'calendar')}
            className="brutal-btn-secondary flex items-center gap-2 notranslate"
            translate="no"
            data-testid="toggle-view-btn"
          >
            {viewMode === 'calendar' ? <List size={20} /> : <CalendarIcon size={20} />}
            {viewMode === 'calendar' ? 'LIST VIEW' : 'CALENDAR VIEW'}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-success border border-black"></div>
          <span className="text-sm font-bold">Available (<span className="notranslate" translate="no">$90/$100</span>)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-amber-100 border-2 border-amber-500 flex items-center justify-center">
            <Star size={12} className="text-amber-600 fill-amber-500" />
          </div>
          <span className="text-sm font-bold">Public Holiday (<span className="notranslate" translate="no">$100</span>)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-gray-200 border border-gray-400 border-dashed"></div>
          <span className="text-sm font-bold">Booked</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-red-200 border-2 border-red-500 flex items-center justify-center">
            <Ban size={14} className="text-red-500" />
          </div>
          <span className="text-sm font-bold">Unavailable</span>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="brutal-card" data-testid="calendar-view">
          <div className="grid grid-cols-7 gap-0 border-2 border-black">
            {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
              <div key={day} className="p-3 bg-accent border-r-2 border-b-2 border-black font-bold text-center text-sm">
                {day}
              </div>
            ))}
            
            {days.map((day, index) => {
              const blackoutCheck = isDateBlackedOut(day);
              const holidayCheck = isPublicHoliday(day);  // Show ALL public holidays (auto + manual)
              const isPastDay = isDayInPast(day);
              const weekendDay = isWeekend(day);
              
              // Premium day = weekend or holiday
              const isPremiumDay = weekendDay || holidayCheck.isHoliday;
              
              return (
                <div
                  key={index}
                  className={`aspect-square border-r-2 border-b-2 border-black p-2 relative transition-colors ${
                    blackoutCheck.isBlackout 
                      ? 'bg-red-100 border-red-300' 
                      : holidayCheck.isHoliday
                        ? 'bg-amber-50 border-amber-300'
                        : weekendDay
                            ? 'bg-secondary/20'
                            : 'hover:bg-yellow-50'
                  }`}
                  title={blackoutCheck.isBlackout ? `Unavailable: ${blackoutCheck.reason}` : holidayCheck.isHoliday ? `🎉 ${holidayCheck.description} ($100)` : weekendDay ? 'Weekend ($100)' : ''}
                  data-testid="calendar-day"
                >
                  {day && (
                    <>
                      <div className={`font-mono font-bold text-sm mb-1 flex items-center gap-1 ${
                        blackoutCheck.isBlackout ? 'text-red-600' : holidayCheck.isHoliday ? 'text-amber-700' : isPastDay ? 'text-gray-400' : ''
                      }`}>
                        {day}
                        {blackoutCheck.isBlackout && <Ban size={12} className="text-red-500" />}
                        {!blackoutCheck.isBlackout && holidayCheck.isHoliday && <Star size={12} className="text-amber-600 fill-amber-500" />}
                      </div>
                      
                      {/* Show Public Holiday label */}
                      {!blackoutCheck.isBlackout && holidayCheck.isHoliday && (
                        <div className="text-[8px] text-amber-700 font-bold truncate mb-1 bg-amber-100 px-1 rounded" title={holidayCheck.description}>
                          {holidayCheck.description}
                        </div>
                      )}
                      
                      {/* Blackout overlay */}
                      {blackoutCheck.isBlackout ? (
                        <div className="space-y-1">
                          <div className="bg-red-200 text-red-700 text-[10px] font-bold px-2 py-1 border border-red-400 text-center">
                            <Ban size={12} className="inline mr-1" />
                            UNAVAILABLE
                          </div>
                          <div className="text-[9px] text-red-600 truncate font-medium px-1" title={blackoutCheck.reason}>
                            {blackoutCheck.reason}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {/* Afternoon slot */}
                          {(!hideBooked || !getSlotStatus(day, 'afternoon')) && (
                            <button
                              onClick={() => handleSlotClick(day, 'afternoon')}
                              className={`w-full text-left touch-manipulation ${
                                getSlotStatus(day, 'afternoon')
                                  ? 'slot-booked cursor-default'
                                  : isSlotInPast(day, 'afternoon')
                                    ? 'bg-white text-gray-500 text-xs font-bold px-2 py-1 border border-gray-300 cursor-default'
                                    : 'slot-available cursor-pointer hover:opacity-80'
                              }`}
                              disabled={!!getSlotStatus(day, 'afternoon') || isSlotInPast(day, 'afternoon')}
                              data-testid="afternoon-slot"
                            >
                              <div className="text-[10px] leading-tight">
                                1PM
                                {getSlotStatus(day, 'afternoon') ? (
                                  <div className="truncate mt-0.5">{getSlotStatus(day, 'afternoon').member_name}</div>
                                ) : (() => {
                                  const restriction = getSlotRestriction(day, 'afternoon');
                                  return restriction.restricted ? (
                                    <div className="truncate mt-0.5 italic text-[9px] text-gray-400">{restriction.reason === 'past' ? 'Past' : restriction.label}</div>
                                  ) : null;
                                })()}
                              </div>
                            </button>
                          )}
                          
                          {/* Evening slot */}
                          {(!hideBooked || !getSlotStatus(day, 'evening')) && (
                            <button
                              onClick={() => handleSlotClick(day, 'evening')}
                              className={`w-full text-left touch-manipulation ${
                                getSlotStatus(day, 'evening')
                                  ? 'slot-booked cursor-default'
                                  : isSlotInPast(day, 'evening')
                                    ? 'bg-white text-gray-500 text-xs font-bold px-2 py-1 border border-gray-300 cursor-default'
                                    : 'slot-available cursor-pointer hover:opacity-80'
                              }`}
                              disabled={!!getSlotStatus(day, 'evening') || isSlotInPast(day, 'evening')}
                              data-testid="evening-slot"
                            >
                              <div className="text-[10px] leading-tight">
                                6PM
                                {getSlotStatus(day, 'evening') ? (
                                  <div className="truncate mt-0.5">{getSlotStatus(day, 'evening').member_name}</div>
                                ) : (() => {
                                  const restriction = getSlotRestriction(day, 'evening');
                                  return restriction.restricted ? (
                                    <div className="truncate mt-0.5 italic text-[9px] text-gray-400">{restriction.reason === 'past' ? 'Past' : restriction.label}</div>
                                  ) : null;
                                })()}
                              </div>
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-4" data-testid="list-view">
          {getListViewSlots().map((slot, idx) => {
            const restriction = getSlotRestriction(slot.day, slot.slotType);
            const isRestricted = restriction.restricted;
            return (
              <div 
                key={idx} 
                className={`brutal-card ${
                  slot.isBlackout 
                    ? 'bg-red-100 border-red-400' 
                    : slot.isHoliday
                      ? 'bg-amber-50 border-amber-300'
                      : ''
                }`} 
                data-testid="list-slot"
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <p className={`font-mono text-lg font-bold ${
                      slot.isBlackout ? 'text-red-600' : slot.isHoliday ? 'text-amber-700' : ''
                    }`}>
                      {formatDate(slot.date)}
                      {slot.isBlackout && <Ban size={16} className="inline ml-2 text-red-500" />}
                      {!slot.isBlackout && slot.isHoliday && <Star size={16} className="inline ml-2 text-amber-600 fill-amber-500" />}
                    </p>
                    {slot.isHoliday && !slot.isBlackout && (
                      <p className="text-xs text-amber-700 font-bold bg-amber-100 inline-block px-2 py-0.5 mt-1 rounded">
                        🎉 {slot.holidayDescription}
                      </p>
                    )}
                    <p className={`text-sm mt-1 ${slot.isBlackout ? 'text-red-600' : ''}`}>
                      {slot.slotType === 'afternoon' ? '1:00 PM - 5:00 PM' : '6:00 PM - 10:00 PM'}
                    </p>
                    {!slot.isBlackout && (
                      <p className="font-mono font-bold mt-1 notranslate" translate="no">${slot.price}</p>
                    )}
                    {slot.booking && (
                      <p className="text-sm text-muted-foreground mt-1">Booked by: <span className="notranslate" translate="no">{slot.booking.kj_name || slot.booking.member_name}</span></p>
                    )}
                    {slot.isBlackout && (
                      <p className="text-sm text-red-600 font-bold mt-1">Reason: {slot.blackoutReason}</p>
                    )}
                    {isRestricted && !slot.booking && !slot.isBlackout && (
                      <p className="text-sm italic text-gray-500 mt-1">
                        {restriction.reason === 'past' ? 'Past slot' : '2-hour rule'}
                      </p>
                    )}
                  </div>
                  {slot.isBlackout ? (
                    <span className="px-4 py-2 text-xs font-bold bg-red-200 text-red-700 border-2 border-red-500 flex items-center gap-1">
                      <Ban size={14} />
                      UNAVAILABLE
                    </span>
                  ) : slot.booking ? (
                    <span className="slot-booked px-4 py-2">BOOKED</span>
                  ) : isRestricted ? (
                    <span className="px-4 py-2 text-xs font-bold text-gray-400 border border-gray-300 bg-gray-50">
                      {restriction.reason === 'past' ? 'PAST' : 'NOT AVAILABLE'}
                    </span>
                  ) : user?.is_admin ? (
                    <span className="px-4 py-2 text-xs font-bold bg-gray-100 text-gray-500 border-2 border-gray-300">
                      VIEW ONLY
                    </span>
                  ) : (
                    <button
                      onClick={() => handleSlotClick(slot.day, slot.slotType)}
                      className="brutal-btn-primary"
                      data-testid="book-slot-btn"
                    >
                      BOOK NOW
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Booking Form Modal */}
      {showBookingForm && selectedSlot && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" 
          data-testid="booking-form-modal"
          onClick={closeBookingForm}
        >
          <div 
            className="brutal-card max-w-lg w-full relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeBookingForm}
              className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center bg-white text-gray-600 hover:text-black rounded-full text-xl font-bold shadow-lg border-2 border-black"
              aria-label="Close modal"
              data-testid="close-form-btn"
            >
              ×
            </button>
            
            <h2 className="text-xl font-syne font-bold mb-4">🎤 BOOK KARAOKE BOOKING</h2>
            
            {/* Slot Summary */}
            <div className="bg-purple-50 border-2 border-purple-300 p-3 mb-4 rounded">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xs font-bold text-purple-600">DATE</p>
                  <p className="font-mono font-bold">{formatDate(selectedSlot.date)}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-purple-600">TIME</p>
                  <p className="font-mono font-bold">
                    {selectedSlot.slot_type === 'afternoon' ? '1PM-5PM' : '6PM-10PM'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold text-purple-600">PRICE</p>
                  <p className="font-mono font-bold text-primary">
                    ${selectedSlot.real_price || calculatePrice(selectedSlot.date, selectedSlot.slot_type)}
                  </p>
                </div>
              </div>
              {selectedSlot.is_premium && selectedSlot.price_reason && (
                <p className="text-xs text-amber-600 font-bold text-center mt-2">
                  ⭐ {selectedSlot.price_reason}
                </p>
              )}
            </div>
            
            {/* Booking Form */}
            <form onSubmit={handleSubmitBooking} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-bold mb-1 flex items-center gap-1">
                  <User size={14} /> Name *
                </label>
                <input
                  type="text"
                  className="brutal-input w-full"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter your full name"
                  required
                  data-testid="name-input"
                />
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-sm font-bold mb-1 flex items-center gap-1">
                  <Mail size={14} /> Email *
                </label>
                <input
                  type="email"
                  className="brutal-input w-full"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="your@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
              
              {/* Mobile */}
              <div>
                <label className="block text-sm font-bold mb-1 flex items-center gap-1">
                  <Phone size={14} /> Mobile *
                </label>
                <input
                  type="tel"
                  className="brutal-input w-full"
                  value={formData.mobile}
                  onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                  placeholder="91234567"
                  required
                  data-testid="mobile-input"
                />
              </div>
              
              {/* Block/Unit */}
              <div>
                <label className="block text-sm font-bold mb-1 flex items-center gap-1">
                  <Home size={14} /> Block/Unit *
                </label>
                <input
                  type="text"
                  className="brutal-input w-full"
                  value={formData.block_unit}
                  onChange={(e) => setFormData({...formData, block_unit: e.target.value})}
                  placeholder="e.g. Blk 212 #01-123"
                  required
                  data-testid="block-unit-input"
                />
              </div>
              
              {/* Booking Type Selection */}
              <div>
                <label className="block text-sm font-bold mb-2">
                  🏠 Booking Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, kj_required: true, kj_preference: ""})}
                    className={`p-3 border-2 text-sm font-bold transition-all ${
                      formData.kj_required
                        ? 'border-purple-500 bg-purple-100 text-purple-800'
                        : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-600'
                    }`}
                    data-testid="booking-type-with-kj"
                  >
                    🎤 With KJ
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, kj_required: false, kj_preference: ""})}
                    className={`p-3 border-2 text-sm font-bold transition-all ${
                      !formData.kj_required
                        ? 'border-indigo-500 bg-indigo-100 text-indigo-800'
                        : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-600'
                    }`}
                    data-testid="booking-type-room-only"
                  >
                    🏠 Room Booking
                  </button>
                </div>
                {!formData.kj_required && (
                  <div className="mt-2 bg-amber-50 border-2 border-amber-300 p-3 text-xs rounded">
                    <p className="font-bold text-amber-800 mb-1">Room Booking (Without KJ):</p>
                    <ul className="text-amber-700 space-y-0.5 list-disc list-inside">
                      <li>Room will be opened 30 min before your session</li>
                      <li>Karaoke equipment is <strong>NOT available</strong></li>
                      <li>Suitable for private gatherings, meetings, etc.</li>
                    </ul>
                  </div>
                )}
              </div>

              {/* KJ Preference Dropdown - only when KJ is required */}
              {formData.kj_required && (
              <div>
                <label className="block text-sm font-bold mb-1">
                  🎤 Preferred KJ
                </label>
                <select
                  className="brutal-input w-full"
                  value={formData.kj_preference}
                  onChange={(e) => setFormData({...formData, kj_preference: e.target.value})}
                  data-testid="kj-select"
                >
                  <option value="">No Preference (Admin will assign)</option>
                  {kjList.map(kj => (
                    <option key={kj.member_id} value={kj.name}>
                      {kj.name} ({kj.type})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select a KJ or leave as &quot;No Preference&quot; for admin to assign
                </p>
              </div>
              )}
              
              {/* Payment Info */}
              <div className="bg-amber-50 border-2 border-amber-500 p-3 text-sm rounded">
                <p className="font-bold text-amber-800">
                  💳 Payment via onePA only
                </p>
                <p className="text-amber-700 mt-1 text-xs">
                  An Admin will contact you with the payment link.
                </p>
              </div>
              
              {/* Booking Limit Info */}
              <div className="bg-blue-50 border-2 border-blue-300 p-3 text-sm rounded">
                <p className="font-bold text-blue-800 text-xs">
                  ℹ️ Booking Limit: Maximum 5 active bookings per email/mobile number
                </p>
              </div>
              
              {/* Terms & Conditions */}
              <div className="bg-gray-50 border border-gray-300 p-2 text-[10px] text-gray-600 rounded max-h-20 overflow-y-auto">
                <p className="font-bold mb-1">Terms & Conditions:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li><strong>Advance Booking:</strong> Bookings must be made at least 2 hours in advance.</li>
                  <li><strong>Payment:</strong> All payments must be made via onePA only.</li>
                  <li><strong>Verification:</strong> Booking confirmed only after Admin verifies payment.</li>
                  <li><strong>Cancellation:</strong> Contact RN Center at least 24 hours before session.</li>
                </ul>
              </div>
              
              {/* Terms Agreement */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5 w-4 h-4"
                  checked={formData.terms_agreed}
                  onChange={(e) => setFormData({...formData, terms_agreed: e.target.checked})}
                  data-testid="terms-checkbox"
                />
                <label className="text-xs">
                  I agree to the Terms & Conditions and confirm my details are correct.
                </label>
              </div>
              
              {/* Payment Policy Notice */}
              <div className="bg-amber-50 border-2 border-amber-400 p-3 text-sm" data-testid="payment-policy-notice">
                <p className="font-bold text-amber-800 mb-1">Payment Policy:</p>
                <ul className="text-amber-900 space-y-1 text-xs list-disc pl-4">
                  <li>An Admin will contact you with the onePA payment link after booking.</li>
                  <li>Please ensure prompt payment after receiving the payment link.</li>
                  <li><strong>Strictly no refunds.</strong></li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeBookingForm}
                  className="brutal-btn-outline flex-1 py-2"
                  disabled={loading}
                  data-testid="cancel-form-btn"
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  className="brutal-btn-primary flex-1 py-2"
                  disabled={loading || !formData.terms_agreed}
                  data-testid="submit-booking-btn"
                >
                  {loading ? 'BOOKING...' : 'CONFIRM BOOKING'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
